import { useEffect, useState, type FormEvent } from "react";
import { api, errorMessage } from "../api.ts";
import { Icon } from "../components/icons.tsx";

export function Login() {
	const [config, setConfig] = useState<{ oidc: boolean; local: boolean; registration: false } | null>(null);
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const controller = new AbortController();
		api.authConfig({ signal: controller.signal }).then(setConfig, (err: unknown) => {
			if (!controller.signal.aborted) setError(errorMessage(err));
		});
		const reason = new URLSearchParams(window.location.search).get("error");
		if (reason === "oidc_failed") setError("Authentik could not complete the login. Try again.");
		else if (reason === "oidc_unavailable") setError("Authentik is temporarily unavailable.");
		return () => controller.abort();
	}, []);

	const submit = async (event: FormEvent): Promise<void> => {
		event.preventDefault();
		setBusy(true);
		setError(null);
		try {
			await api.login(username, password);
			window.location.replace("/");
		} catch (err) {
			setError(errorMessage(err));
			setBusy(false);
		}
	};

	return (
		<main className="login-shell">
			<section className="login-card" aria-labelledby="login-title">
				<header className="login-brand">
					<span className="login-mark"><Icon name="card" /></span>
					<span>Bonus<b>box</b></span>
				</header>
				<div className="login-intro">
					<p className="eyebrow">PRIVATE SHOPPING TOOL</p>
					<h1 id="login-title">Sign in</h1>
					<p>Your baskets and AH cards stay separate from every other user.</p>
				</div>

				{error !== null && <div className="login-error" role="alert"><Icon name="alert" /> {error}</div>}

				{config?.oidc && (
					<a className="login-oidc" href={`/api/auth/oidc/start?return=${encodeURIComponent("/")}`}>
						<span className="authentik-glyph">a</span>
						<span><b>Continue with Authentik</b><small>Use your tiebe.me identity</small></span>
						<Icon name="chevron" />
					</a>
				)}

				{config?.oidc && config.local && <div className="login-divider"><span>or local account</span></div>}

				{config?.local && (
					<form className="login-form" onSubmit={(event) => void submit(event)}>
						<label>
							<span>Username</span>
							<input
								autoComplete="username"
								autoCapitalize="none"
								value={username}
								onChange={(event) => setUsername(event.target.value)}
								required
							/>
						</label>
						<label>
							<span>Password</span>
							<input
								type="password"
								autoComplete="current-password"
								value={password}
								onChange={(event) => setPassword(event.target.value)}
								required
							/>
						</label>
						<button className="login-submit" type="submit" disabled={busy || username === "" || password === ""}>
							{busy ? "Signing in…" : "Sign in"}
						</button>
					</form>
				)}

				<p className="login-no-signup">
					Accounts are created by the administrator. There is no public registration.
				</p>
			</section>
		</main>
	);
}
